import {useMemo, useState} from "react";
import {Popover, UnstyledButton} from "@mantine/core";
import {useTranslation} from "react-i18next";
import {useLocation} from "react-router-dom";
import IconChevronDown from "../../../assets/chevron-down.svg?react";
import type {ChecklistToc, CollectionDataResponse} from "../types";
import {getAllRelatedDocuments} from "./DocumentsList";
import {FileRefRow} from "./components/FileRefRow";
import {DocumentGeneratorRefRow} from "./components/DocumentGeneratorRefRow";
import styles from "./document-selector-field.module.css";

type DocumentSelectorFieldProps = {
    collectionData?: CollectionDataResponse;
    viewFilteredToc: ChecklistToc;
    effectiveRootNodeId: string;
    onToggleDocumentSelected: (topicId: string, selected: boolean) => void;
    activeDocumentId?: string;
};

/**
 * Narrow-viewport replacement for the desktop documents sidebar list. A select-
 * styled trigger opens a dropdown where each document carries a checkbox
 * (include in generation) and a label that navigates to that document.
 *
 * The parity-critical logic is reused from `DocumentsList`
 * (`getAllRelatedDocuments` eligibility/grouping) and the per-item row
 * components (`DocumentGeneratorRefRow` / `FileRefRow`); only the dropdown
 * container layout is owned here, so it can evolve independently of the desktop
 * list.
 */
export const DocumentSelectorField = ({
    collectionData,
    viewFilteredToc,
    effectiveRootNodeId,
    onToggleDocumentSelected,
    activeDocumentId,
}: DocumentSelectorFieldProps) => {
    const {t} = useTranslation();
    const location = useLocation();
    const [opened, setOpened] = useState(false);

    const checklistBasePath = useMemo(() => {
        const parts = location.pathname.split("/");
        // /type/name/id → first 4 segments
        return parts.slice(0, 4).join("/");
    }, [location.pathname]);

    const documents = useMemo(() => {
        if (!effectiveRootNodeId || !viewFilteredToc[effectiveRootNodeId])
            return null;
        return getAllRelatedDocuments(viewFilteredToc, effectiveRootNodeId);
    }, [viewFilteredToc, effectiveRootNodeId]);

    const checkItemsWithDocuments = useMemo(() => {
        if (!documents) return [];
        return documents.orderedTopLevelCheckItems.filter(
            (item) =>
                (documents.checkItemDocuments[item.id]?.size ?? 0) > 0 ||
                (documents.checkItemFiles[item.id]?.size ?? 0) > 0,
        );
    }, [documents]);

    const activeName = useMemo(() => {
        if (!documents || !activeDocumentId) return null;
        for (const set of Object.values(documents.checkItemDocuments)) {
            for (const doc of set) {
                if (doc.topicId === activeDocumentId) return doc.name;
            }
        }
        return null;
    }, [documents, activeDocumentId]);

    if (!documents || checkItemsWithDocuments.length === 0) {
        return (
            <div className={styles.field}>
                <span className={styles.noDocuments}>
                    {t("Info.NoAvailableDocuments")}
                </span>
            </div>
        );
    }

    return (
        <div className={styles.field}>
            <Popover
                opened={opened}
                onChange={setOpened}
                position="bottom"
                width="target"
                shadow="md"
                withinPortal
            >
                <Popover.Target>
                    <UnstyledButton
                        className={styles.trigger}
                        onClick={() => setOpened((o) => !o)}
                        aria-expanded={opened}
                    >
                        <span className={styles.triggerLabel}>
                            {activeName ??
                                t("Action.ChooseDocument", {
                                    defaultValue: "Choose document",
                                })}
                        </span>
                        <IconChevronDown
                            className={styles.triggerChevron}
                            aria-hidden="true"
                        />
                    </UnstyledButton>
                </Popover.Target>

                <Popover.Dropdown className={styles.dropdown}>
                    {checkItemsWithDocuments.map((checkItem) => {
                        const files = Array.from(
                            documents.checkItemFiles[checkItem.id] ?? [],
                        );
                        const docs = Array.from(
                            documents.checkItemDocuments[checkItem.id] ?? [],
                        );

                        return (
                            <div key={checkItem.id} className={styles.group}>
                                <h3 className={styles.groupHeading}>
                                    {checkItem.name}
                                </h3>
                                <ul className={styles.list}>
                                    {files.map((fd) => (
                                        <li
                                            key={fd.fileItem.topicId}
                                            className={styles.row}
                                        >
                                            <FileRefRow
                                                fileItem={fd.fileItem}
                                                relatedItem={
                                                    fd.relatedItem ?? undefined
                                                }
                                            />
                                        </li>
                                    ))}
                                    {docs.map((doc) => (
                                        <li
                                            key={doc.topicId}
                                            className={`${styles.row}${
                                                activeDocumentId === doc.topicId
                                                    ? ` ${styles.activeRow}`
                                                    : ""
                                            }`}
                                            // Close on navigation (anchor click),
                                            // but not when toggling the checkbox.
                                            onClick={(e) => {
                                                if (
                                                    (
                                                        e.target as HTMLElement
                                                    ).closest("a")
                                                ) {
                                                    setOpened(false);
                                                }
                                            }}
                                        >
                                            <DocumentGeneratorRefRow
                                                relatedItem={doc}
                                                collectionData={collectionData}
                                                onToggle={
                                                    onToggleDocumentSelected
                                                }
                                                checklistBasePath={
                                                    checklistBasePath
                                                }
                                            />
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        );
                    })}
                </Popover.Dropdown>
            </Popover>
        </div>
    );
};
